import type { KpiComparison } from "@types";
import { resolveKpiComparison } from "@utils/kpiComparison";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Notification } from "@rio-cloud/rio-uikit";
import type { DashboardFilter } from "../../types/dashboard";
import {
  deriveTotalRecords,
  resolveFetchContextLabel,
  TRANSACTION_PAGE_SIZE,
  type TransactionStatusPeriod,
} from "./transactionScreenUtils";

export interface TransactionListServices {
  getTableData(
    startDate: string,
    endDate: string,
    type: string,
    query: { page: number; size: number; searchTerm?: string },
    filters: DashboardFilter
  ): Promise<any>;
  getDRESummary(startDate: string, endDate: string, filters: DashboardFilter, searchTerm?: string): Promise<any>;
}

export type TransactionKpiComparisons = Partial<Record<string, KpiComparison | null>>;

const normalizeKpiComparisons = (raw: any, summary: any): TransactionKpiComparisons => {
  if (!raw) return {};

  const periods = { currentPeriod: summary?.currentPeriod, previousPeriod: summary?.previousPeriod };

  return Object.entries(raw).reduce<TransactionKpiComparisons>((acc, [chave, valor]) => {
    acc[chave] = resolveKpiComparison(valor, periods);
    return acc;
  }, {});
};

export interface UseTransactionListDataParams<TRow, TTotals> {
  transactionType: string;
  services: TransactionListServices;
  startDate: string;
  endDate: string;
  isTreeReady: boolean;
  listFilters: DashboardFilter;
  previousMonthFilters: DashboardFilter;
  search: string;
  loadErrorMessage: string;
  mapRow: (item: any) => TRow;
  calculateStatusTotals: (rows: TRow[]) => TTotals;
  selectSummaryTotal: (data: any) => number | undefined | null;
  selectSummaryComparisons?: (data: any) => TransactionKpiComparisons | undefined | null;
  selectSummaryStatusTotals?: (data: any, period: TransactionStatusPeriod) => TTotals | undefined | null;
}

export interface TransactionListData<TRow, TTotals> {
  tableData: TRow[];
  totalRecords: number;
  isLoadingTable: boolean;
  isLoadingKpis: boolean;
  isLoadingMore: boolean;
  totalAmount: number;
  previousTotalAmount: number | null;
  previousKpiTotalsByStatus: TTotals | null;
  kpiTotalsByStatus: TTotals;
  kpiComparisons: TransactionKpiComparisons;
  fetchInitial: () => Promise<void>;
  handleLoadMore: () => void;
}

export const useTransactionListData = <TRow, TTotals>(
  params: UseTransactionListDataParams<TRow, TTotals>
): TransactionListData<TRow, TTotals> => {
  const { transactionType, startDate, endDate, isTreeReady, listFilters, previousMonthFilters, search } = params;

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoadingTable, setIsLoadingTable] = useState(true);
  const [isLoadingKpis, setIsLoadingKpis] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [kpiComparisons, setKpiComparisons] = useState<TransactionKpiComparisons>({});
  const [previousTotalAmount, setPreviousTotalAmount] = useState<number | null>(null);
  const [previousKpiTotalsByStatus, setPreviousKpiTotalsByStatus] = useState<TTotals | null>(null);
  const [summaryStatusTotals, setSummaryStatusTotals] = useState<TTotals | null>(null);
  const [tableData, setTableData] = useState<TRow[]>([]);

  const requestIdRef = useRef(0);
  const appliedSearchRef = useRef(search);

  // Os totais por status sao do periodo inteiro e vem do backend; a soma das linhas
  // carregadas e so fallback — ela cresce conforme o "Carregar mais" e nunca bate com o total.
  const kpiTotalsByStatus = useMemo(
    () => summaryStatusTotals ?? paramsRef.current.calculateStatusTotals(tableData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [summaryStatusTotals, tableData]
  );

  const fetchTableData = useCallback(
    async (page: number, append = false) => {
      const setLoading = append ? setIsLoadingMore : setIsLoadingTable;
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      setLoading(true);

      try {
        const response = await paramsRef.current.services.getTableData(
          startDate,
          endDate,
          transactionType,
          {
            page,
            size: TRANSACTION_PAGE_SIZE,
            searchTerm: paramsRef.current.search,
          },
          listFilters
        );

        if (requestId !== requestIdRef.current) return;

        const normalizedPayload = response?.data ?? response;
        const content = Array.isArray(normalizedPayload?.content) ? normalizedPayload.content : [];
        const formattedRows: TRow[] = content.map((item: any) => paramsRef.current.mapRow(item));

        setTableData((prev) => (append ? [...prev, ...formattedRows] : formattedRows));
        setTotalRecords(deriveTotalRecords(normalizedPayload, formattedRows.length));
        setCurrentPage(page);
      } catch {
        if (requestId === requestIdRef.current) Notification.error(paramsRef.current.loadErrorMessage);
      } finally {
        setLoading(false);
      }
    },
    [endDate, listFilters, startDate, transactionType]
  );

  const getDashboard = useCallback(async () => {
    setIsLoadingKpis(true);
    try {
      const response = await paramsRef.current.services.getDRESummary(
        startDate,
        endDate,
        listFilters,
        paramsRef.current.search?.trim() || undefined
      );
      setTotalAmount(paramsRef.current.selectSummaryTotal(response.data) || 0);
      setSummaryStatusTotals(paramsRef.current.selectSummaryStatusTotals?.(response.data, "current") ?? null);
      setKpiComparisons(
        normalizeKpiComparisons(paramsRef.current.selectSummaryComparisons?.(response.data), response.data)
      );
    } catch (error) {
      console.error("Erro ao carregar totais do dashboard:", error);
      setTotalAmount(0);
      setSummaryStatusTotals(null);
      setKpiComparisons({});
    } finally {
      setIsLoadingKpis(false);
    }
  }, [endDate, listFilters, startDate]);

  const fetchPreviousKpis = useCallback(async () => {
    try {
      const [summaryResponse, tableResponse] = await Promise.all([
        paramsRef.current.services.getDRESummary(
          previousMonthFilters.startDate!,
          previousMonthFilters.endDate!,
          previousMonthFilters,
          paramsRef.current.search?.trim() || undefined
        ),
        paramsRef.current.services.getTableData(
          previousMonthFilters.startDate!,
          previousMonthFilters.endDate!,
          transactionType,
          { page: 0, size: TRANSACTION_PAGE_SIZE },
          previousMonthFilters
        ),
      ]);

      setPreviousTotalAmount(paramsRef.current.selectSummaryTotal(summaryResponse.data) || 0);

      const summaryTotals = paramsRef.current.selectSummaryStatusTotals?.(summaryResponse.data, "current");

      if (summaryTotals) {
        setPreviousKpiTotalsByStatus(summaryTotals);
        return;
      }

      const normalizedPayload = tableResponse?.data ?? tableResponse;
      const content = Array.isArray(normalizedPayload?.content) ? normalizedPayload.content : [];
      setPreviousKpiTotalsByStatus(
        paramsRef.current.calculateStatusTotals(content.map((item: any) => paramsRef.current.mapRow(item)))
      );
    } catch (error) {
      console.error("Erro ao carregar KPIs do mês anterior:", error);
      setPreviousTotalAmount(null);
      setPreviousKpiTotalsByStatus(null);
    }
  }, [previousMonthFilters, transactionType]);

  const fetchInitial = useCallback(async () => {
    const results = await Promise.allSettled([getDashboard(), fetchTableData(0), fetchPreviousKpis()]);

    results.forEach((result, index) => {
      if (result.status === "rejected") {
        console.error(`Erro ao carregar ${resolveFetchContextLabel(index)}:`, result.reason);
      }
    });
  }, [fetchTableData, getDashboard, fetchPreviousKpis]);

  const handleLoadMore = () => {
    if (isLoadingMore || tableData.length >= totalRecords) {
      return;
    }

    fetchTableData(currentPage + 1, true);
  };

  useEffect(() => {
    if (!isTreeReady) return;

    appliedSearchRef.current = paramsRef.current.search;

    const loadData = async () => {
      setCurrentPage(0);
      setTableData([]);
      setTotalRecords(0);
      await fetchInitial();
    };

    loadData();
  }, [isTreeReady, startDate, endDate, fetchInitial]);

  useEffect(() => {
    if (!isTreeReady || appliedSearchRef.current === search) return;

    appliedSearchRef.current = search;
    setCurrentPage(0);
    setTableData([]);
    setTotalRecords(0);
    fetchTableData(0);
    getDashboard();
  }, [isTreeReady, search, fetchTableData, getDashboard]);

  return {
    tableData,
    totalRecords,
    isLoadingTable,
    isLoadingKpis,
    isLoadingMore,
    totalAmount,
    previousTotalAmount,
    previousKpiTotalsByStatus,
    kpiTotalsByStatus,
    kpiComparisons,
    fetchInitial,
    handleLoadMore,
  };
};
